import { BreakpointObserver } from "@angular/cdk/layout";
import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FX_MEDIA_QUERIES, FxBreakpoint, pickResponsiveValue } from "./breakpoints";

@Directive({
  standalone: false,
  selector: "[fxLayout]"
})
export class FxLayoutDirective implements OnInit, OnChanges, OnDestroy {
  @Input() fxLayout?: string;
  @Input("fxLayout.xs") fxLayoutXs?: string;
  @Input("fxLayout.sm") fxLayoutSm?: string;
  @Input("fxLayout.md") fxLayoutMd?: string;
  @Input("fxLayout.gt-xs") fxLayoutGtXs?: string;
  @Input("fxLayout.gt-sm") fxLayoutGtSm?: string;
  @Input("fxLayout.gt-md") fxLayoutGtMd?: string;

  private readonly destroy$ = new Subject<void>();
  private readonly activeBreakpoints = new Set<FxBreakpoint>();

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.breakpointObserver
      .observe(Object.values(FX_MEDIA_QUERIES))
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.activeBreakpoints.clear();
        (Object.keys(FX_MEDIA_QUERIES) as FxBreakpoint[]).forEach((breakpoint) => {
          if (state.breakpoints[FX_MEDIA_QUERIES[breakpoint]]) {
            this.activeBreakpoints.add(breakpoint);
          }
        });
        this.applyLayout();
      });

    this.applyLayout();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.applyLayout();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyLayout(): void {
    const layoutValue = pickResponsiveValue(
      this.fxLayout,
      {
        "xs": this.fxLayoutXs,
        "sm": this.fxLayoutSm,
        "md": this.fxLayoutMd,
        "gt-xs": this.fxLayoutGtXs,
        "gt-sm": this.fxLayoutGtSm,
        "gt-md": this.fxLayoutGtMd
      },
      (breakpoint) => this.activeBreakpoints.has(breakpoint)
    );

    const direction = this.normalizeDirection(layoutValue);
    this.renderer.setStyle(this.host.nativeElement, "display", "flex");
    this.renderer.setStyle(this.host.nativeElement, "flex-direction", direction);
  }

  private normalizeDirection(value?: string): "row" | "column" {
    if (!value) {
      return "row";
    }

    const [firstToken] = value.trim().toLowerCase().split(/\s+/);
    return firstToken === "column" ? "column" : "row";
  }
}
